import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { ApiError } from '../api/client'
import {
  ALLOWED_EXTENSIONS,
  MAX_UPLOAD_BYTES,
  SURFACE_LABELS,
  aspectRatio,
  formatBytes,
  formatDuration,
  isVideo,
  mediaApi,
  type MediaAsset,
  type SurfaceCompatibility,
} from '../api/media'

type Banner = { kind: 'success' | 'error'; text: string }

/**
 * Reglerna bor på servern (PlatformMediaSpecs). Här översätter vi bara utfallet.
 * Färgen på badgen är aldrig ensam betydelsebärare — texten säger samma sak.
 */
function surfaceStatus(s: SurfaceCompatibility) {
  if (!s.canPublish) return { text: 'Blockerad', kind: 'error' as const }
  if (s.issues.length > 0) return { text: 'Med anmärkning', kind: 'warn' as const }
  return { text: 'Klar', kind: 'ok' as const }
}

export function MediaPage() {
  const [assets, setAssets] = useState<MediaAsset[]>([])
  const [banner, setBanner] = useState<Banner | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const load = useCallback(async () => {
    try {
      setAssets(await mediaApi.list())
    } catch {
      setBanner({ kind: 'error', text: 'Kunde inte hämta mediabiblioteket.' })
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const uploading = progress !== null

  async function upload(file: File) {
    setBanner(null)

    // Klientvalidering är bekvämlighet, inte skydd. Servern kontrollerar allt igen,
    // och ffprobe läser filens byten — inte dess ändelse.
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])) {
      setBanner({
        kind: 'error',
        text: `Filtypen ".${ext}" stöds inte. Tillåtna: ${ALLOWED_EXTENSIONS.join(', ')}.`,
      })
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setBanner({
        kind: 'error',
        text: `Filen är ${formatBytes(file.size)}. Största tillåtna är ${formatBytes(MAX_UPLOAD_BYTES)}.`,
      })
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setProgress(0)
    try {
      const asset = await mediaApi.upload(file, setProgress, controller.signal)
      setAssets((prev) => [asset, ...prev])
      setBanner({ kind: 'success', text: `${file.name} uppladdad och validerad.` })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setBanner({ kind: 'error', text: 'Uppladdningen avbröts.' })
      } else if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null
        setBanner({
          kind: 'error',
          // Servern avvisar t.ex. en .exe som döpts om till .mp4 — ffprobe läser byten.
          text: body?.error ?? `Uppladdningen misslyckades (HTTP ${err.status}).`,
        })
      } else {
        setBanner({ kind: 'error', text: (err as Error).message })
      }
    } finally {
      setProgress(null)
      abortRef.current = null
    }
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // så samma fil kan väljas igen efter ett fel
    if (file) void upload(file)
  }

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragging(false)
    if (uploading) return
    const file = e.dataTransfer.files?.[0]
    if (file) void upload(file)
  }

  async function onDelete(asset: MediaAsset) {
    if (!window.confirm('Ta bort filen permanent? Den kan inte återställas.')) return
    try {
      await mediaApi.remove(asset.id)
      setAssets((prev) => prev.filter((a) => a.id !== asset.id))
      setBanner({ kind: 'success', text: 'Filen borttagen.' })
    } catch {
      setBanner({ kind: 'error', text: 'Kunde inte ta bort filen.' })
    }
  }

  return (
    <main className="page">
      <h1>Media</h1>
      <p className="muted">
        Ladda upp video eller bild att publicera. Filerna lagras utanför webbservern
        och läses av <code>ffprobe</code>, som avgör vad de faktiskt innehåller.
      </p>

      {banner && (
        <div role="status" className={`banner banner-${banner.kind === 'success' ? 'success' : 'error'}`}>
          {banner.text}
        </div>
      )}

      <section className="card">
        <h2>Ladda upp</h2>

        {/*
          <label> som släppyta: ett klick eller Enter/Blanksteg på det fokuserade
          fältet öppnar filväljaren. Fältet är visuellt dolt men INTE display:none —
          det måste kunna nå tangentbordsfokus.
        */}
        <label
          className={`dropzone${dragging ? ' dropzone-active' : ''}${uploading ? ' dropzone-busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <input
            type="file"
            className="sr-only"
            accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
            onChange={onFileInput}
            disabled={uploading}
          />
          <span className="dropzone-title">
            {/* Färg är aldrig ensam betydelsebärare — texten byts också. */}
            {dragging ? 'Släpp filen här' : 'Dra en fil hit, eller tryck för att välja'}
          </span>
          <span className="muted small">
            {ALLOWED_EXTENSIONS.join(', ')} · max {formatBytes(MAX_UPLOAD_BYTES)}
          </span>
        </label>

        {uploading && (
          <div className="upload-progress">
            <progress value={progress ?? 0} max={100} aria-labelledby="upload-status" />
            <p id="upload-status" aria-live="polite" className="small">
              {progress} % uppladdat
              {progress === 100 && ' — validerar med ffprobe…'}
            </p>
            <button type="button" className="link" onClick={() => abortRef.current?.abort()}>
              Avbryt
            </button>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Mediabibliotek</h2>

        {assets.length === 0 ? (
          <p className="muted small no-accounts">Inget uppladdat än.</p>
        ) : (
          <ul className="media-list">
            {assets.map((a) => {
              const ratio = aspectRatio(a.width, a.height)
              const duration = formatDuration(a.durationSeconds)
              return (
                <li key={a.id} className="media-row">
                  <div className="media-preview">
                    {isVideo(a.contentType) ? (
                      // preload="metadata": hämta inte hela videon för en miniatyr.
                      // Ingen autoplay — rörelse får aldrig starta oombedd.
                      <video src={`/api/media/${a.id}`} controls preload="metadata" />
                    ) : (
                      <img src={`/api/media/${a.id}`} alt="Förhandsvisning av uppladdad bild" />
                    )}
                  </div>

                  <div className="media-meta">
                    <div className="platform-head">
                      <div className="account-name">{a.contentType}</div>
                      {a.isUniversalMaster && (
                        <span className="badge badge-ok">Master för alla kortformsytor</span>
                      )}
                    </div>
                    <dl className="media-facts">
                      {a.width && a.height && (
                        <>
                          <dt>Upplösning</dt>
                          <dd>{a.width}×{a.height}{ratio && ` (${ratio})`}</dd>
                        </>
                      )}
                      {duration && (<><dt>Längd</dt><dd>{duration}</dd></>)}
                      <dt>Storlek</dt>
                      <dd>{formatBytes(a.sizeBytes)}</dd>
                      <dt>Källa</dt>
                      <dd>{a.source === 'VideoAi' ? 'AI-genererad' : 'Manuell uppladdning'}</dd>
                      <dt>Uppladdad</dt>
                      <dd>{new Date(a.createdAt).toLocaleString('sv-SE')}</dd>
                    </dl>

                    <ul className="surface-list">
                      {a.surfaces.map((s) => {
                        const status = surfaceStatus(s)
                        return (
                          <li key={s.surface} className="surface-row">
                            <span className="surface-name">{SURFACE_LABELS[s.surface]}</span>
                            <span className={`badge badge-${status.kind}`}>{status.text}</span>
                            {s.issues.length > 0 && (
                              <ul className="surface-issues">
                                {s.issues.map((i) => (
                                  <li key={i.message} className="muted small">{i.message}</li>
                                ))}
                              </ul>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>

                  <button type="button" className="link media-delete" onClick={() => void onDelete(a)}>
                    Ta bort
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </main>
  )
}
